"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  tenant_id: number;
  avatar_url?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    // Check if user is logged in on mount
    const token = localStorage.getItem("access_token");
    const userData = localStorage.getItem("user");

    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error("Failed to parse user data:", error);
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    // Redirect logic.
    //
    // Public paths fall into two buckets:
    //   exact-match — visited as-is (no children)
    //   prefix      — also covers /share/citations/abc, /share/ai-visibility/uuid
    //
    // Critical: /pricing must be public so the Phase-5 landing page is
    // reachable to logged-out visitors. /share/* and the marketing
    // helpers must also bypass auth — they're explicitly designed for
    // unauthenticated end-clients clicking a share link.
    const publicExact = new Set<string>([
      "/",
      "/login",
      "/register",
      "/signup",
      "/forgot-password",
      "/reset-password",
      "/payment/success",
      "/payment/cancelled",
      "/pricing",
      "/privacy",
      "/terms",
    ]);
    const publicPrefixes = ["/share/", "/pricing/"];
    const isPublicPath =
      publicExact.has(pathname) ||
      publicPrefixes.some((p) => pathname.startsWith(p));

    if (!loading) {
      if (!user && !isPublicPath) {
        // Not logged in and trying to access protected route
        router.push("/login");
      } else if (user && (pathname === "/login" || pathname === "/register")) {
        // Logged in and trying to access login/register (but allow payment pages)
        router.push("/");
      }
    }
  }, [user, loading, pathname, router]);

  const login = (token: string, userData: User) => {
    localStorage.setItem("access_token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    router.push("/");
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    router.push("/login");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
